import { DefaultModal } from 'UI';
import { CourseDto } from 'api/generated';
import { css } from 'styled-components';

import { BundleBuilderContent } from './components/BundleBuilderContent';
import { useBundleBuilderCatalog } from './useBundleBuilderCatalog';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** The course whose Upgrade button opened this. Used to preselect and for analytics. */
  sourceCourseId: string | null;
  /** Called once the learner's picks resolve to a bundle; hands off to the pricing modal. */
  onContinue: (bundle: CourseDto) => void;
};

/**
 * The Bundle Builder, shown instead of BundleUpgradeOptionsModal for courses configured with
 * upsellMode='builder'.
 *
 * A full-page modal rather than a route, deliberately: it replaces the legacy upsell in the same
 * surface, so the existing handoff to ChooseProductModal keeps working and the learner never
 * leaves the page they were on. ChooseProductModal already renders this way.
 *
 * Scope is intentionally narrow — this is the in-LMS confirm-and-swap screen, not the marketing
 * sales page. Show the package the learner is on, let them change it, then check out.
 */
const BundleBuilderModal = ({ isOpen, onClose, sourceCourseId, onContinue }: Props) => {
  const { catalog, isLoading, error } = useBundleBuilderCatalog(isOpen);

  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onClose}
      title="Build your bundle"
      modalCSS={ModalCSS}
      containerCSS={ContainerCSS}>
      <BundleBuilderContent
        catalog={catalog}
        isLoading={isLoading}
        error={error}
        sourceCourseId={sourceCourseId}
        onContinue={onContinue}
        onClose={onClose}
      />
    </DefaultModal>
  );
};

export default BundleBuilderModal;

// Matches ChooseProductModal: the modal fills the viewport rather than floating, so there is no
// transform to fight.
//
// overflow is hidden rather than auto on purpose — the builder is an app shell, not a document. It
// scrolls its own middle section and keeps the action bar pinned to the bottom of the screen; if
// this container scrolled too, the bar would drift away with the content.
const ContainerCSS = css`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 22px;
  border-radius: 0;
  overflow: hidden;
`;

const ModalCSS = css`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transform: none;
  margin-right: 0;
  width: 100vw;
  height: 100vh;
  padding: 0;
`;
